import { useEffect, useRef } from 'react';
import type { ClientPackVerification } from '@mcpanel/core';

const labels = { preflight: '事前チェック', vm: 'テスト用の VM', launch: '起動テスト' };
export function ClientPackLogs({ check }: { check: ClientPackVerification }) {
  const container = useRef<HTMLDivElement>(null);
  const failure = useRef<HTMLDivElement>(null);
  const stages = check.stages ?? { preflight: [], vm: [], launch: check.logs };
  const signature = JSON.stringify(stages);
  useEffect(() => {
    if (check.state === 'running' && container.current) container.current.scrollTop = container.current.scrollHeight;
  }, [signature, check.state]);
  const download = () => {
    const content = Object.entries(stages)
      .map(([stage, lines]) => '[' + labels[stage as keyof typeof labels] + ']\n' + lines.join('\n'))
      .join('\n\n');
    const reports = check.crashReports.map((report) => report.name + '\n' + report.content).join('\n\n');
    const url = URL.createObjectURL(
      new Blob([check.reason ? check.reason + '\n\n' : '', content, '\n\n', reports], {
        type: 'text/plain;charset=utf-8',
      }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'client-pack-' + check.hash + '.log';
    link.click();
    URL.revokeObjectURL(url);
  };
  const entries = Object.entries(stages).flatMap(([stage, lines]) =>
    lines.map((line, index) => ({ stage, index, line })),
  );
  const failed =
    check.state === 'failed'
      ? entries.findLast((entry) => check.reason && entry.line.includes(check.reason.slice(0, 2000)))
      : undefined;
  return (
    <>
      {check.reason && (
        <p className={check.state === 'failed' ? 'alert warn' : 'small muted'}>
          {check.reason}
          {check.state === 'failed' && (
            <button className="btn small" onClick={() => failure.current?.scrollIntoView({ block: 'center' })}>
              失敗の行へ移動
            </button>
          )}
        </p>
      )}
      <div className="row">
        <strong>ログ</strong>
        <button className="btn small" onClick={download}>
          ログを保存（.log）
        </button>
      </div>
      <div
        ref={container}
        role="log"
        aria-label="クライアント起動テストのログ"
        tabIndex={0}
        style={{
          maxHeight: 'min(60vh, 36rem)',
          overflow: 'auto',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {Object.entries(stages).map(([stage, lines]) => (
          <section key={stage}>
            <h5>{labels[stage as keyof typeof labels]}</h5>
            {lines.map((line, index) => (
              <div key={index} ref={failed?.stage === stage && failed.index === index ? failure : undefined}>
                {line}
              </div>
            ))}
            {!lines.length && <div className="muted">実行記録はまだありません</div>}
            {stage === 'launch' &&
              check.crashReports.map((report) => (
                <div key={report.name}>
                  <strong>{report.name}</strong>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{report.content}</pre>
                </div>
              ))}
          </section>
        ))}
        {check.state === 'failed' && !failed && <div ref={failure}>{check.reason}</div>}
      </div>
    </>
  );
}
